import React, { useEffect, useState } from "react";
import { Pencil, MapPin } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import {
  FooterContent2,
  UpdateFooterContent2Data,
  footerContent2Service,
} from "../../../services/footer/maps";

const FooterContent2Management: React.FC = () => {
  const [footerContent, setFooterContent] = useState<FooterContent2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFooterContent, setEditingFooterContent] = useState<FooterContent2 | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchFooterContent = async () => {
    try {
      setLoading(true);
      const response = await footerContent2Service.getFooterContent2();
      setFooterContent(response || []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch footer content");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterContent();
  }, []);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setEditingFooterContent(null);
    setIsModalOpen(false);
  };

  const filteredFooterContent = footerContent.filter((content) =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.link_maps.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUpdateFooterContent = async () => {
    if (!editingFooterContent) return;

    if (!editingFooterContent.title.trim() || !editingFooterContent.link_maps.trim()) {
      showError("Title and Google Maps Link cannot be empty");
      return;
    }

    try {
      const updatedData: UpdateFooterContent2Data = {
        id: editingFooterContent.id,
        title: editingFooterContent.title,
        link_maps: editingFooterContent.link_maps,
      };
      await footerContent2Service.updateFooterContent2(updatedData);
      await fetchFooterContent();
      showSuccess("Footer content updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update footer content");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header seperti Article Management */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center">
                  <MapPin size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Footer Content 2 Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage Google Maps link for footer</p>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by title or Google Maps link..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">TITLE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">GOOGLE MAPS LINK</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFooterContent.map((content, index) => (
                    <tr key={content.id} className="hover:bg-gray-50 transition-colors">
                      {/* ID dengan nomor urut */}
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {index + 1}
                        </div>
                      </td>

                      {/* Title */}
                      <td className="px-8 py-6 font-medium text-gray-800">
                        {content.title}
                      </td>

                      {/* Google Maps Link */}
                      <td className="px-8 py-6 text-gray-600">
                        <a
                          href={content.link_maps}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 hover:underline break-all line-clamp-2 max-w-md"
                        >
                          {content.link_maps}
                        </a>
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-6">
                        <div className="flex justify-end">
                          <button
                            onClick={() => {
                              setEditingFooterContent(content);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredFooterContent.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-20 text-center text-gray-400">
                        No footer content found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal - Mirip dengan Article */}
      {isModalOpen && editingFooterContent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">Edit Google Maps Link</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={editingFooterContent.title}
                  onChange={(e) =>
                    setEditingFooterContent({ ...editingFooterContent, title: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-blue-500"
                  placeholder="Enter title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Google Maps Link</label>
                <input
                  type="text"
                  value={editingFooterContent.link_maps}
                  onChange={(e) =>
                    setEditingFooterContent({ ...editingFooterContent, link_maps: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-blue-500"
                  placeholder="https://maps.google.com/..."
                />
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <button
                onClick={resetForm}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFooterContent}
                className="flex-1 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-medium transition-all"
              >
                Update Maps Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterContent2Management;